import React, { useState, useEffect, useContext, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import Dropzone from 'react-dropzone';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import LabelName from 'components/UI/LabelName/LabelName';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import { sanitizeHtmlColorContent } from 'utils/utils';
import { convertToHtml } from 'utils/utility_functions/convertToHtml';
import { useSecureMessageContext } from '../Context/SecureMessageContext';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import useCompanionCase from 'modules/cases/hooks/useCompanionCase';
import { useCaseUserClientDetails } from 'hooks/useCaseUserClientDetails';
import { getClientUserList } from 'services/users/userServices';
import { generateFullName } from 'utils/generateFullNameUtils';
import { sortByKey } from 'utils/utils';
import { UserDetailsContext } from 'context/userDetailsContext';
import { WebSocketConnectionContext } from 'context/WebSocketConnectionContext';
import { getTenantIdFromLS, getUserInfoFromLS } from 'utils/localStorageUtils';
import { validateUpload } from 'services/fileManager/fileManagerServices';
import { FILEUPLOAD_S3, FILEUPLOAD_S3_LEVEL, SetS3Config } from 'config/amplify_config';
import { getAutoSave } from 'services/messageServices';
import { Storage } from 'aws-amplify';
import { Auth } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'universal-cookie';
import './CompanionCaseMessageModal.scss';

const cookie = new Cookies();

function CompanionCaseMessageModal({ show, setShow, caseDetails }) {
  const { fetchCaseMessage } = useSecureMessageContext();
  const { addToast } = useToast();
  const { companionList } = useCompanionCase(caseDetails?.case_id);
  const { caseUserClientList, loadCaseUserClientDetails } = useCaseUserClientDetails();
  const userContext = useContext(UserDetailsContext);
  const websocketContext = useContext(WebSocketConnectionContext);

  const [selectedContacts, setSelectedContacts] = useState({});
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [quickSelectPrimary, setQuickSelectPrimary] = useState(false);
  const [expandedCases, setExpandedCases] = useState({});
  const [companionCasesWithContacts, setCompanionCasesWithContacts] = useState([]);
  const [mainCaseWithContacts, setMainCaseWithContacts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [sending, setSending] = useState(false);
  const [hasAutoSaveData, setHasAutoSaveData] = useState(false);
  const [mainCaseValidationError, setMainCaseValidationError] = useState('');
  const [hasErrorBeenShown, setHasErrorBeenShown] = useState(false);
  const errorMessageRef = useRef(null);

  // Transform main case data from API to the format needed for the UI
  const transformMainCaseToUI = (caseUserClientList, caseDetails) => {
    if (!caseUserClientList?.length || !caseDetails) return null;

    const contacts = caseUserClientList
      .filter((client) => client.is_create_client_portal === true)
      .map((client) => ({
        contact_id: client.contact_id || client.user_id,
        first_name: client.first_name,
        last_name: client.last_name,
        middle_name: client.middle_name,
        email: client.email,
        is_primary_contact: client.is_primary_contact,
        is_create_client_portal: client.is_create_client_portal,
        full_name: generateFullName(client),
        client_cognito_username: client.client_cognito_username,
      }));

    return {
      case_id: caseDetails.case_id,
      case_name: caseDetails.case_description?.case_name || 'Main Case',
      contacts: sortByKey(contacts, 'first_name'),
    };
  };

  // Transform companionList from API to the format needed for the UI
  const transformCompanionListToUI = (companionList) => {
    if (!companionList?.length) return [];

    return companionList
      .filter((companionCase) => {
        // Filter out cases where is_lead = true
        // Cases with is_lead = false or is_lead not present should be displayed normally
        return companionCase.is_lead !== true;
      })
      .map((companionCase) => {
        const contacts =
          companionCase.lawft_clients
            ?.filter((client) => client.is_create_client_portal === true)
            ?.map((client) => ({
              contact_id: client.contact_id || client.user_id,
              first_name: client.first_name,
              last_name: client.last_name,
              middle_name: client.middle_name,
              email: client.email,
              is_primary_contact: client.is_primary_contact,
              is_create_client_portal: client.is_create_client_portal,
              full_name: generateFullName(client),
              client_cognito_username: client.client_cognito_username,
            })) || [];

        return {
          case_id: companionCase.case_id,
          case_name: companionCase.case_name,
          contacts: sortByKey(contacts, 'first_name'),
        };
      });
  };

  // Initialize with API data when companionList changes
  useEffect(() => {
    if (companionList?.length) {
      const transformedData = transformCompanionListToUI(companionList);
      setCompanionCasesWithContacts(transformedData);

      // Initialize selected contacts - start with all unchecked
      const initialSelection = {};
      const initialExpanded = {};

      transformedData.forEach((companionCase) => {
        initialSelection[companionCase.case_id] = {};
        initialExpanded[companionCase.case_id] = true; // Expand all cases by default

        companionCase.contacts?.forEach((contact) => {
          initialSelection[companionCase.case_id][contact.contact_id] = false;
        });
      });

      setSelectedContacts(initialSelection);
      setExpandedCases(initialExpanded);
    }
  }, [companionList]);

  // Initialize main case data when caseUserClientList changes
  useEffect(() => {
    if (caseUserClientList?.length && caseDetails) {
      const transformedMainCase = transformMainCaseToUI(caseUserClientList, caseDetails);
      setMainCaseWithContacts(transformedMainCase);

      // Initialize selected contacts for main case - start with all unchecked
      if (transformedMainCase) {
        setSelectedContacts((prev) => {
          const newSelection = { ...prev };
          newSelection[transformedMainCase.case_id] = {};
          newSelection[transformedMainCase.case_id] =
            transformedMainCase.contacts?.reduce((acc, contact) => {
              acc[contact.contact_id] = false;
              return acc;
            }, {}) || {};
          return newSelection;
        });

        setExpandedCases((prev) => ({
          ...prev,
          [transformedMainCase.case_id]: true, // Expand main case by default
        }));
      }
    }
  }, [caseUserClientList, caseDetails]);

  // Reload data when modal is opened (show becomes true)
  useEffect(() => {
    if (show) {
      // Load auto-saved data when modal opens
      getAutoSave(caseDetails?.case_id)
        .then((res) => {
          if (Object.keys(res?.data).length) {
            // Restore auto-saved data
            if (res.data.subject) {
              setSubject(res.data.subject);
            }
            if (res.data.message) {
              setMessage(res.data.message);
            }
            if (res.data.selectedContacts) {
              setSelectedContacts(res.data.selectedContacts);
            }
            if (res.data.files) {
              setFiles(res.data.files);
            }
            setHasAutoSaveData(true);
          }
        })
        .catch((error) => {
          console.log('No auto-saved data found or error loading:', error);
        });

      // Fetch main case users when modal opens
      if (caseDetails?.case_id) {
        loadCaseUserClientDetails(caseDetails.case_id, false, false, true, false, false, false, false, false, false);
      }

      if (companionList?.length) {
        const transformedData = transformCompanionListToUI(companionList);
        setCompanionCasesWithContacts(transformedData);

        // Initialize selected contacts - start with all unchecked
        const initialSelection = {};
        const initialExpanded = {};

        transformedData.forEach((companionCase) => {
          initialSelection[companionCase.case_id] = {};
          initialExpanded[companionCase.case_id] = true; // Expand all cases by default

          companionCase.contacts?.forEach((contact) => {
            initialSelection[companionCase.case_id][contact.contact_id] = false;
          });
        });

        setSelectedContacts(initialSelection);
        setExpandedCases(initialExpanded);
      }
    }
  }, [show, caseDetails?.case_id]);

  // Auto-save functionality when data changes
  useEffect(() => {
    let autoSaveInterval = null;

    if (show && hasUnsavedData()) {
      autoSaveInterval = setInterval(() => {
        const autoSaveData = {
          subject: subject,
          message: message,
          selectedContacts: selectedContacts,
          files: files,
        };
        sendAutoSaveReq(autoSaveData);
        setHasAutoSaveData(true);
      }, 5000); // Auto-save every 5 seconds
    }

    return () => {
      if (autoSaveInterval) {
        clearInterval(autoSaveInterval);
      }
    };
  }, [show, subject, message, selectedContacts, files]);

  // Handle quick select primary contacts
  const handleQuickSelectPrimary = () => {
    setQuickSelectPrimary(true);

    const newSelection = { ...selectedContacts };

    // Handle main case primary contacts
    if (mainCaseWithContacts) {
      if (!newSelection[mainCaseWithContacts.case_id]) {
        newSelection[mainCaseWithContacts.case_id] = {};
      }

      mainCaseWithContacts.contacts?.forEach((contact) => {
        if (contact.is_primary_contact) {
          newSelection[mainCaseWithContacts.case_id][contact.contact_id] = true;
        }
      });

      // Clear validation error when primary contacts are selected
      setMainCaseValidationError('');
    }

    // Handle companion cases primary contacts
    companionCasesWithContacts?.forEach((companionCase) => {
      if (!newSelection[companionCase.case_id]) {
        newSelection[companionCase.case_id] = {};
      }

      companionCase.contacts?.forEach((contact) => {
        if (contact.is_primary_contact) {
          newSelection[companionCase.case_id][contact.contact_id] = true;
        }
      });
    });

    setSelectedContacts(newSelection);
  };

  // Handle individual contact selection
  const handleContactSelection = (caseId, contactId, checked) => {
    setSelectedContacts((prev) => ({
      ...prev,
      [caseId]: {
        ...(prev[caseId] || {}),
        [contactId]: checked,
      },
    }));

    // Handle main case contact selection/deselection
    if (mainCaseWithContacts && caseId === mainCaseWithContacts.case_id) {
      if (checked) {
        // Clear validation error when a main case contact is selected
        setMainCaseValidationError('');
      } else {
        // Check if all main case contacts are now unselected
        const updatedSelection = {
          ...selectedContacts,
          [caseId]: {
            ...(selectedContacts[caseId] || {}),
            [contactId]: checked,
          },
        };

        const mainCaseContacts = updatedSelection[mainCaseWithContacts.case_id] || {};
        const hasAnyMainCaseSelected = Object.values(mainCaseContacts).some((selected) => selected === true);

        // If no main case contacts are selected and error was previously shown, show it again
        if (!hasAnyMainCaseSelected && hasErrorBeenShown) {
          setMainCaseValidationError('Please select atleast one contact from the current case');
        }
      }
    }
  };

  // Toggle case expansion
  const toggleCaseExpansion = (caseId) => {
    setExpandedCases((prev) => ({
      ...prev,
      [caseId]: !prev[caseId],
    }));
  };

  // Handle file upload
  const onFileDrop = (acceptedFiles) => {
    setFiles((prev) => [...prev, ...acceptedFiles]);
  };

  // Remove file
  const removeFile = (fileToRemove) => {
    setFiles((prev) => prev.filter((file) => file.path !== fileToRemove.path));
  };

  // Get total selected contacts count
  const getTotalSelectedContacts = () => {
    let count = 0;
    Object.values(selectedContacts).forEach((caseContacts) => {
      Object.values(caseContacts).forEach((selected) => {
        if (selected) count++;
      });
    });
    return count;
  };

  // Check if at least one main case contact is selected
  const hasMainCaseContactSelected = () => {
    if (!mainCaseWithContacts) return false;

    const mainCaseContacts = selectedContacts[mainCaseWithContacts.case_id] || {};
    return Object.values(mainCaseContacts).some((selected) => selected === true);
  };

  // File upload function
  async function fileUpload() {
    let attachments = [];
    if (files && files.length > 0) {
      const filesData = Object.values(files);
      const totalSize = filesData.reduce((acc, f) => acc + f.size, 0);

      let totalUploaded = 0; // Track uploaded bytes cumulatively

      try {
        await validateUpload(totalSize);
      } catch (err) {
        console.error('Validation failed:', err);
        let errorMsg = err?.response?.data?.message || err?.response?.data || err?.message || 'File validation failed';
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, errorMsg);
        return null;
      }

      try {
        const tenantId = (await cookie.get('tenantId')) || '';
        const moduleName = 'messages';
        const userId = userContext?.userDetails?.firm_user_id;
        const case_Id = caseDetails?.case_id;

        // Use an object to track per-file uploaded bytes, so later progress don't overlap
        let perFileUploaded = filesData.map(() => 0);

        for (let i = 0; i < filesData.length; i++) {
          const element = filesData[i];
          SetS3Config(FILEUPLOAD_S3, FILEUPLOAD_S3_LEVEL);
          let user = await Auth.currentAuthenticatedUser();

          let response = await Storage.put(
            `${moduleName}/${tenantId}/${userId}/${case_Id ? case_Id + '/' : ''}${uuidv4()}#$#${encodeURIComponent(element.name)}`,
            element,
            {
              contentType: element.type,
              progressCallback: (progress) => {
                const { loaded } = progress;

                // Calculate incremental uploaded bytes for this file
                let delta = loaded - perFileUploaded[i];

                if (delta > 0) {
                  // Update per file uploaded bytes
                  perFileUploaded[i] = loaded;

                  // Update total uploaded bytes
                  totalUploaded += delta;

                  // Calculate overall upload percentage
                  let percent = Math.floor((totalUploaded / totalSize) * 100);
                  // Clamp percent to max 100
                  percent = percent > 100 ? 100 : percent;

                  // Update UI progress
                  setUploadPercentage(percent);
                }
              },
              useAccelerateEndpoint: true,
            }
          );

          if (response.key) {
            let file_type = element.type.toString();
            let file_details = {
              file_name: element.name,
              display_name: element.name,
              size: element.size.toString(),
              file_path: 'public/' + response.key,
              file_type: file_type,
              created_by: user.attributes,
            };
            attachments.push(file_details);
          }
        }
        setUploadPercentage(100); // ensure 100% at end
        return attachments;
      } catch (err) {
        console.log(err);
        return attachments;
      }
    } else {
      return attachments;
    }
  }

  // Auto-save functionality
  const sendAutoSaveReq = async (sendData, is_delete = false) => {
    const reqData = {
      user_id: userContext.firmDetails.current_user,
      tenant_id: userContext.firmDetails.tenant_id,
      auto_save_type: 'companion_case_message',
      action: 'autosave',
      type: 'autosave_message',
      is_delete,
      case_id: caseDetails?.case_id,
    };
    userContext.firmDetails.firm_user_id && (reqData.firm_user_id = userContext.firmDetails.firm_user_id);
    userContext.firmDetails.contact_id && (reqData.contact_id = userContext.firmDetails.contact_id);
    reqData.data = sendData;
    await websocketContext.sendMessage(reqData);
  };

  // Build companion_case_config object in the required format
  const buildCompanionCaseConfig = () => {
    const companionCaseConfig = {};

    Object.keys(selectedContacts).forEach((caseId) => {
      const caseContacts = selectedContacts[caseId];
      const selectedContactsForCase = [];

      Object.keys(caseContacts).forEach((contactId) => {
        if (caseContacts[contactId]) {
          // Find the contact details from main case or companion cases
          let contact = null;

          // Check main case first
          if (mainCaseWithContacts && mainCaseWithContacts.case_id === caseId) {
            contact = mainCaseWithContacts.contacts?.find((c) => c.contact_id === contactId);
          } else {
            // Check companion cases
            const companionCase = companionCasesWithContacts.find((c) => c.case_id === caseId);
            contact = companionCase?.contacts?.find((c) => c.contact_id === contactId);
          }

          if (contact) {
            selectedContactsForCase.push({
              contact_id: contact.contact_id,
              client_cognito_username: contact.client_cognito_username,
            });
          }
        }
      });

      if (selectedContactsForCase.length > 0) {
        companionCaseConfig[caseId] = selectedContactsForCase;
      }
    });

    return companionCaseConfig;
  };

  // Build recipients array from selected contacts (only from main case for message_to)
  const buildRecipients = () => {
    const recipients = [];

    // Only include selected contacts from the main case in message_to
    if (mainCaseWithContacts) {
      const mainCaseContacts = selectedContacts[mainCaseWithContacts.case_id] || {};

      Object.keys(mainCaseContacts).forEach((contactId) => {
        if (mainCaseContacts[contactId]) {
          const contact = mainCaseWithContacts.contacts?.find((c) => c.contact_id === contactId);

          if (contact) {
            recipients.push({
              recipient_name: contact.full_name,
              recipient_id: contact.client_cognito_username,
              client_cognito_username: contact.client_cognito_username,
              firm_user_id: '',
              contact_id: contact.contact_id,
              email: contact.email,
              access_level: 'client',
            });
          }
        }
      });
    }

    return recipients;
  };

  // Helper function to check if message has actual content (strips HTML tags)
  const hasMessageContent = (htmlMessage) => {
    if (!htmlMessage) return false;
    // Create a temporary div to strip HTML tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlMessage;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    return textContent.trim().length > 0;
  };

  // Handle send messages
  const handleSendMessages = async () => {
    // Clear any previous validation errors
    setMainCaseValidationError('');

    if (!subject.trim()) {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Please enter a subject');
      return;
    }

    if (!hasMessageContent(message)) {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Please enter a message');
      return;
    }

    const totalSelected = getTotalSelectedContacts();
    if (totalSelected === 0) {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Please select at least one contact');
      return;
    }

    // Check if at least one main case contact is selected
    if (!hasMainCaseContactSelected()) {
      setMainCaseValidationError('Please select atleast one contact from the current case');
      setHasErrorBeenShown(true);

      // Scroll to error message after a short delay to ensure it's rendered
      setTimeout(() => {
        if (errorMessageRef.current) {
          errorMessageRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);

      return;
    }

    setSending(true);

    try {
      // Upload files first
      let uploadedAttachments = [];
      if (files?.length > 0) {
        setUploadPercentage(1); // Show progress immediately
        uploadedAttachments = await fileUpload();
        if (uploadedAttachments === null) {
          setSending(false);
          return; // Upload failed
        }
      }

      // Build the companion_case_config object
      const companionCaseConfig = buildCompanionCaseConfig();

      // Build recipients array
      const recipients = buildRecipients();

      // Build the message data structure
      const tenantId = getTenantIdFromLS();
      const userInfo = getUserInfoFromLS();

      let sendData = {
        is_global_module: false, // This is case-specific
        recipients: recipients.map((r) => r.recipient_id), // Only main case recipients
        action: 'message',
        type: 'secure_message',
        data: {
          secure_message_type: 'thread',
          tenant_id: tenantId,
          body: {
            message_to: recipients,
            groups: [],
            subject: subject,
            message: message,
            attachment: uploadedAttachments,
            event_attachment: [],
            task_attachment: [],
            case_id: caseDetails?.case_id,
            case_name: caseDetails?.case_description?.case_name || 'TBD',
            companion_case_config: companionCaseConfig,
          },
        },
        case_id: caseDetails?.case_id,
        creator_first_name: userInfo?.attributes?.['custom:first_name'] || '',
        creator_last_name: userInfo?.attributes?.['custom:last_name'] || '',
        creator_email: userInfo?.attributes?.email || '',
      };

      console.log('🚀 Final Send Data:', sendData);

      // Send message via WebSocket
      await websocketContext.sendMessage(sendData);

      // Refresh messages and close modal
      fetchCaseMessage({});
      handleClose();
    } catch (error) {
      console.error('Error sending messages:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to send messages');
    } finally {
      setSending(false);
      setUploadPercentage(0);
    }
  };

  // Check if there's unsaved data
  const hasUnsavedData = () => {
    // Check if any recipients are selected
    const hasSelectedRecipients = getTotalSelectedContacts() > 0;

    // Check if there's subject, message content or files
    const hasContent = subject.trim() || hasMessageContent(message) || files.length > 0;

    return hasSelectedRecipients || hasContent;
  };

  // Handle close with confirmation
  const clearUnsavedData = () => {
    if (hasUnsavedData() || hasAutoSaveData) {
      confirmDialog({
        message: 'Do you want to draft save this message?',
        header: 'Discard',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          // Save current data as draft before closing
          const draftData = {
            subject: subject,
            message: message,
            selectedContacts: selectedContacts,
            files: files,
          };
          sendAutoSaveReq(draftData);
          handleClose();
        },
        reject: () => {
          handleClose();
          //To clear the unsaved data
          sendAutoSaveReq(null, true);
        },
      });
    } else {
      handleClose();
    }
  };

  // Handle close
  const handleClose = () => {
    // Blur any focused element to prevent focus trap issues
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    setShow(false);
    setSubject('');
    setMessage('');
    setFiles([]);
    setQuickSelectPrimary(false);
    setSelectedContacts({});
    setExpandedCases({});
    setCompanionCasesWithContacts([]);
    setMainCaseWithContacts(null);
    setLoading(false);
    setUploadPercentage(0);
    setSending(false);
    setHasAutoSaveData(false);
    setMainCaseValidationError('');
    setHasErrorBeenShown(false);
  };

  // Header component to match Create New Message modal
  const headerComponent = (data) => {
    return (
      <div>
        <i
          className="icon-window-filled modal-close-icon F-size14 pointer"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (data?.onHide) {
              data.onHide();
            }
          }}
        ></i>
      </div>
    );
  };

  return (
    <Dialog
      header={headerComponent}
      visible={show}
      onHide={clearUnsavedData}
      className="create-msg-popup"
      style={{ width: '90vw', maxWidth: '800px', minWidth: '600px' }}
      closable={false}
      resizable={false}
      draggable={false}
      modal={true}
      blockScroll={true}
    >
      <div className="companion-case-message-content">
        {/* Title */}
        <h5 className="modal-title">Send Message to Companion Cases</h5>

        {/* Description Box */}
        <div className="description-box d-flex align-items-center">
          <i className="pi pi-info-circle me-2"></i>
          <p className="F-size12 mb-0">
            This allows you to send the same message to users across all companion cases. Each message is sent individually. This is not a
            group message.{' '}
          </p>
        </div>

        {/* Select Recipients Section */}
        <div className="select-recipients-section mt-3">
          <div className="section-header">
            <div className="section-title-with-error">
              <LabelName required={true} className="section-title">
                Select Recipients
              </LabelName>
              {mainCaseValidationError && (
                <div className="text-danger mt-2" ref={errorMessageRef}>
                  {mainCaseValidationError}
                </div>
              )}
            </div>
            <Button
              label="Quick Select Primary Contacts"
              icon="pi pi-check"
              className={`p-button p-button-secondary ${quickSelectPrimary ? 'active' : ''}`}
              onClick={handleQuickSelectPrimary}
            />
          </div>
          <div className="selected-count">{getTotalSelectedContacts()} contacts selected</div>

          {/* Main Case Section */}
          {mainCaseWithContacts && (
            <div key={mainCaseWithContacts.case_id} className="case-section card-bg">
              <div className="case-name-head">
                <div className="case-info">
                  <div className="case-name">{mainCaseWithContacts.case_name}</div>
                </div>
              </div>

              <div className="contacts-list">
                {mainCaseWithContacts.contacts?.map((contact) => {
                  const isChecked = selectedContacts[mainCaseWithContacts.case_id]?.[contact.contact_id] || false;

                  return (
                    <div key={contact.contact_id} className={`contact-item ${contact.is_primary_contact ? 'primary-contact' : ''}`}>
                      <input
                        type="checkbox"
                        id={`contact-${mainCaseWithContacts.case_id}-${contact.contact_id}`}
                        checked={isChecked}
                        onChange={(e) => handleContactSelection(mainCaseWithContacts.case_id, contact.contact_id, e.target.checked)}
                        className="contact-checkbox"
                      />
                      <label htmlFor={`contact-${mainCaseWithContacts.case_id}-${contact.contact_id}`} className="contact-label">
                        <span className="contact-name">{contact.full_name}</span>
                        {contact.is_primary_contact && (
                          <div className="blue-tag">
                            <span className="blue-tag-value text-nowrap">Primary</span>
                          </div>
                        )}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Companion Cases Section */}
          {companionCasesWithContacts?.map((companionCase) => (
            <div key={companionCase.case_id} className="case-section card-bg">
              <div className="case-name-head">
                <div className="case-info">
                  <div className="case-name">{companionCase.case_name}</div>
                </div>
              </div>

              <div className="contacts-list">
                {companionCase.contacts?.map((contact) => {
                  const isChecked = selectedContacts[companionCase.case_id]?.[contact.contact_id] || false;

                  return (
                    <div key={contact.contact_id} className={`contact-item ${contact.is_primary_contact ? 'primary-contact' : ''}`}>
                      <input
                        type="checkbox"
                        id={`contact-${companionCase.case_id}-${contact.contact_id}`}
                        checked={isChecked}
                        onChange={(e) => handleContactSelection(companionCase.case_id, contact.contact_id, e.target.checked)}
                        className="contact-checkbox"
                      />
                      <label htmlFor={`contact-${companionCase.case_id}-${contact.contact_id}`} className="contact-label">
                        <span className="contact-name">{contact.full_name}</span>
                        {contact.is_primary_contact && (
                          <div className="blue-tag">
                            <span className="blue-tag-value text-nowrap">Primary</span>
                          </div>
                        )}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Subject Section */}
        <div className="subject-section mt-3">
          <LabelName required={true} className="section-title">
            Subject:
          </LabelName>
          <div className="subject-input-wrapper">
            <TextSnippetWrapper
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="w-100 input-shadow"
            />
          </div>
        </div>

        {/* Message and Attachments Section */}
        <LabelName required={true} className="section-title">
          Message:
        </LabelName>
        <div className="message-attachments-section">
          <div className="message-section">
            <TextSnippetWrapper
              type="editor"
              value={sanitizeHtmlColorContent(message)}
              style={{ height: '150px' }}
              onChange={(e) => setMessage(convertToHtml(e))}
            />
          </div>

          <div className="attachments-section">
            <Dropzone
              onDrop={onFileDrop}
              accept="application/pdf, application/msword, .docx, .doc, audio/*, video/*, image/*, .heic, .heif, image/heic, image/heif"
            >
              {({ getRootProps, getInputProps }) => (
                <div {...getRootProps()} className="attachment-dropzone">
                  <input {...getInputProps()} />
                  <div className="attachment-area">
                    <i className="pi pi-paperclip attachment-icon"></i>
                    <span className="attachment-text">Click to attach files (optional)</span>
                  </div>
                </div>
              )}
            </Dropzone>

            {files.length > 0 && (
              <div className="file-list">
                {files.map((file, index) => (
                  <div key={file.path || index} className="file-item">
                    <span className="file-name">{file.name}</span>
                    <i className="icon-delete icon-red pointer" onClick={() => removeFile(file)}></i>
                  </div>
                ))}
                {uploadPercentage > 0 && uploadPercentage < 100 && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${uploadPercentage}%` }}></div>
                    </div>
                    <span className="progress-text">{uploadPercentage}% uploaded</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="footer-btn justify-content-between">
          <div className="d-flex">{/* Empty div to match Create New Message layout */}</div>
          <div className="d-flex">
            <Button
              type="button"
              label="Cancel"
              className="p-button p-button-secondary outline me-2"
              onClick={clearUnsavedData}
              disabled={sending}
            />
            <Button
              type="button"
              label={sending ? 'Sending...' : 'Send Messages'}
              className={`p-button p-button-secondary ${mainCaseValidationError ? 'validation-error' : ''}`}
              onClick={handleSendMessages}
              disabled={!subject.trim() || !hasMessageContent(message) || getTotalSelectedContacts() === 0 || sending}
              loading={sending}
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export default CompanionCaseMessageModal;
